import { MenuRowShortcutKbd } from '@/components/ui/shortcut-kbd'
import { cn } from '@/utils'
import type { ZoomMenuOption } from '../types/zoom-menu-option'

/** Option row: 248×32 outer (232×20 content + spacing/2 horizontal + spacing/1.5 vertical). */
const ZOOM_MENU_ROW_CLASS =
  'flex h-8 w-full box-border items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm leading-5'

const ZOOM_MENU_ROW_PRESET_CLASS = `${ZOOM_MENU_ROW_CLASS} tabular-nums`

const ROW_INTERACTION_CLASS =
  'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none'

const ROW_DISABLED_CLASS =
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'

const COMMAND_ROWS = {
  actualSize: { label: 'Actual Size (1:1)', shortcutKey: 'actualSize' },
  fit: { label: 'Fit to spread', shortcutKey: 'fitToSpread' },
  zoomSelection: { label: 'Zoom to Selection', shortcutKey: 'zoomToSelection' },
} as const

function resolveZoomOptionRowClassName(
  option: ZoomMenuOption,
  highlighted: boolean,
  disabled: boolean | undefined,
  matchesDraft: boolean
): string {
  if (option.kind === 'preset') {
    return cn(
      ZOOM_MENU_ROW_PRESET_CLASS,
      ROW_INTERACTION_CLASS,
      matchesDraft && 'bg-accent/60 text-accent-foreground',
      highlighted && 'bg-accent text-accent-foreground ring-1 ring-inset ring-ring/40'
    )
  }

  if (option.kind === 'actualSize') {
    return cn(
      ZOOM_MENU_ROW_CLASS,
      ROW_INTERACTION_CLASS,
      highlighted && 'bg-accent text-accent-foreground'
    )
  }

  return cn(
    ZOOM_MENU_ROW_CLASS,
    ROW_INTERACTION_CLASS,
    ROW_DISABLED_CLASS,
    highlighted && !disabled && 'bg-accent text-accent-foreground'
  )
}

type ZoomOptionRowProps = {
  option: ZoomMenuOption
  index: number
  highlighted: boolean
  /** Only Fit to spread and Zoom to Selection can be disabled; undefined for the other rows. */
  disabled?: boolean
  /** Preset rows: the draft's rounded value equals this preset (tinted as "current"). */
  matchesDraft: boolean
  onHighlight: (index: number) => void
  onActivate: (index: number) => void
}

/** One `role="option"` row of the zoom listbox. */
export function ZoomOptionRow({
  option,
  index,
  highlighted,
  disabled,
  matchesDraft,
  onHighlight,
  onActivate,
}: ZoomOptionRowProps) {
  return (
    <button
      id={`zoom-option-${index}`}
      type="button"
      role="option"
      aria-selected={highlighted}
      aria-disabled={disabled}
      disabled={disabled}
      data-zoom-option-index={index}
      className={resolveZoomOptionRowClassName(option, highlighted, disabled, matchesDraft)}
      onMouseDown={(event) => event.preventDefault()}
      onMouseEnter={() => {
        if (disabled) return
        onHighlight(index)
      }}
      onClick={() => {
        if (disabled) return
        onActivate(index)
      }}
    >
      {option.kind === 'preset' ? (
        <>{option.percent}%</>
      ) : (
        <>
          <span className="flex-1">{COMMAND_ROWS[option.kind].label}</span>
          <MenuRowShortcutKbd ariaHidden shortcutKey={COMMAND_ROWS[option.kind].shortcutKey} />
        </>
      )}
    </button>
  )
}
